import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

function AttachmentModal({ report, onClose }) {
  if (!report) return null;

  const src = `data:${report.attachment_mime || "image/*"};base64,${
    report.attachment
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-lg border border-white/10 bg-slate-950 p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
          <h3 className="text-lg font-bold text-white">
            {report.attachment_name || "Report Attachment"}
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            X
          </button>
        </div>
        {report.attachment_mime === "application/pdf" ? (
          <iframe src={src} title="Report attachment" className="h-[75vh] w-full rounded" />
        ) : (
          <img
            src={src}
            alt="Report attachment"
            className="min-h-0 w-full flex-1 rounded object-contain"
          />
        )}
      </div>
    </div>
  );
}

export default function TenantReportHistory() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [attachmentReport, setAttachmentReport] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/reports/my");
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const statusClass = (status) => {
    if (status === "Resolved") return "border-teal-300/40 bg-teal-500/15 text-teal-200";
    if (status === "In Progress") return "border-sky-300/40 bg-sky-500/15 text-sky-200";
    return "border-amber-300/40 bg-amber-500/15 text-amber-200";
  };

  const filteredReports = useMemo(() => {
    const query = searchTerm;

    return reports.filter((report) => {
      const matchesStatus =
        statusFilter === "all" || report.status === statusFilter;
      const matchesSearch = !query || report.category === query;
      return matchesStatus && matchesSearch;
    });
  }, [reports, searchTerm, statusFilter]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans">
      <video data-workspace-background="true" aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
        src={bgVideo}
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="absolute inset-0 bg-slate-950/86"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80"></div>

      <AttachmentModal
        report={attachmentReport}
        onClose={() => setAttachmentReport(null)}
      />

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-16 text-white sm:px-6 md:py-10 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Maintenance Reports
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              My Reports
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Track submitted requests, progress timestamps, and admin
              resolution notes.
            </p>
          </div>
        </div>

        <div className="mb-5 flex flex-col gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl lg:flex-row lg:items-center lg:justify-between">
          <select aria-label="Filter by category" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="rounded-lg bg-white px-3 py-3 text-sm text-slate-900">
            <option value="">All categories</option>
            {[...new Set(reports.map((report) => report.category).filter(Boolean))].map((category) => <option key={category}>{category}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            {["all", "Pending", "In Progress", "Resolved"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  statusFilter === status
                    ? "bg-teal-600 text-white"
                    : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading reports...
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/15 bg-slate-950/70 py-10 text-center text-slate-300">
            No reports found.
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredReports.map((report) => (
              <article
                key={report.id}
                className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">
                        {report.category || "Other"}
                      </span>

                    </div>
                    <h2 className="mt-3 text-2xl font-bold text-white">
                      {report.subject}
                    </h2>
                    <p className="mt-2 text-sm text-slate-400">
                      Submitted {new Date(report.created_at).toLocaleString()}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
                      Stage: {String(report.workflow_stage === "assigned" ? "in_progress" : (report.workflow_stage || "new")).replaceAll("_", " ")}
                    </p>
                  </div>
                  <span
                    className={`w-fit rounded-full border px-3 py-1 text-xs font-semibold ${statusClass(
                      report.status
                    )}`}
                  >
                    {report.status}
                  </span>
                </div>

                <p className="mt-4 whitespace-pre-wrap rounded-lg bg-white/5 p-4 text-sm leading-6 text-slate-300">
                  {report.message}
                </p>

                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      In Progress
                    </p>
                    <p className="mt-1 text-slate-200">
                      {report.in_progress_at
                        ? new Date(report.in_progress_at).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Resolved
                    </p>
                    <p className="mt-1 text-slate-200">
                      {report.resolved_at
                        ? new Date(report.resolved_at).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Target Resolution
                    </p>
                    <p className="mt-1 text-slate-200">
                      {report.target_resolution_at
                        ? new Date(report.target_resolution_at).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Attachment
                    </p>
                    {report.attachment ? (
                      <button
                        onClick={() => setAttachmentReport(report)}
                        className="mt-1 font-semibold text-teal-200 hover:text-teal-100"
                      >
                        View Attachment
                      </button>
                    ) : (
                      <p className="mt-1 text-slate-200">-</p>
                    )}
                  </div>
                </div>

                {report.resolution_notes && (
                  <div className="mt-4 rounded-lg border border-teal-300/20 bg-teal-500/10 p-4 text-sm text-teal-100">
                    <p className="font-semibold">Resolution Notes</p>
                    <p className="mt-1 whitespace-pre-wrap">
                      {report.resolution_notes}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
